<template>
  <div
    ref="iconWrapperRef"
    class="wl-icon flex"
    :class="[
      {
        'is-boxed rounded-2xl': isBoxed,
        'has-current-color': hasCurrentColor,
      },
      `size-${size}`,
    ]"
  >
    <AsyncIconComponent class="relative z-10" :name />
  </div>
</template>

<script lang="ts" setup>
import { type IconProps, IconSize } from "./types";
import { computed, defineAsyncComponent, type Component } from "vue";

const {
  name,
  hasCurrentColor = true,
  size = IconSize.MEDIUM,
} = defineProps<IconProps>();

/* v8 ignore start */
const modules = import.meta.glob<{ default: Component }>(
  ["@customIcons/**/*.svg", "@defaultIcons/**/*.svg"],
  {
    eager: false,
  },
);

const sizeDir = computed(() => {
  switch (size) {
    case IconSize.EXTRA_SMALL:
      return 12;
    case IconSize.SMALL:
      return 16;
    case IconSize.MEDIUM:
    default:
      return 20;
    case IconSize.LARGE:
      return 24;
  }
});

const EmptyComponent = { render: (): null => null };

const AsyncIconComponent = computed(() => {
  return (
    // Otherwise computed doesn't get triggered.
    name &&
    defineAsyncComponent(async (): Promise<Component> => {
      const defaultIconPath = `../../libs/discover-components/src/assets/icons/base/${sizeDir.value}/${name}.svg`;
      const customIconPath = `/src/assets/icons/custom/${sizeDir.value}/${name}.svg`;
      let file: { default: Component } | undefined;

      try {
        // NOTE: Glob imports with aliases do not preserve their alias in the
        // resulting object, making it difficult to map and find the icon.
        // See: https://github.com/vitejs/vite/issues/12180
        //
        // The logic for this is isolated in code that will be removed when built
        // for production by dead-code elimination.
        // See: https://vitejs.dev/guide/env-and-mode.html#production-replacement
        if (import.meta.env.DEV) {
          const storybookIconPath = `/src/assets/icons/base/${sizeDir.value}/${name}.svg`;

          const icon =
            modules[customIconPath] ??
            modules[defaultIconPath] ??
            modules[storybookIconPath];
          file = await icon();
        } else {
          const icon = modules[customIconPath] ?? modules[defaultIconPath];
          file = await icon();
        }
      } catch {
        // eslint-disable-next-line no-console
        console.error(
          "Could not load the following icon:",
          `${sizeDir.value}/${name}.svg`,
        );
      }

      if (!file) {
        return EmptyComponent;
      }

      return file;
    })
  );
});
/* v8 ignore stop */
</script>

<style>
@reference "@theme";

.wl-icon {
  svg {
    @apply z-0 h-auto w-full;
  }

  &.is-boxed {
    @apply border-theme-transparent flex size-8 items-center justify-center border;
  }

  &.size-xs {
    svg {
      @apply size-3;
    }
  }

  &.size-sm {
    svg {
      @apply size-4;
    }
  }

  &.size-md {
    svg {
      @apply size-5;
    }
  }

  &.size-lg {
    svg {
      @apply size-6;
    }
  }

  &.has-current-color {
    rect,
    path,
    circle {
      &[fill] {
        @apply fill-current;
      }

      &[stroke] {
        @apply stroke-current;
      }
    }
  }
}
</style>
